import { Flex, Tooltip, Typography } from "antd";
import { ColumnType } from "antd/es/table";
import {
  BID_STATUSES,
  DIRECTION_TEXT,
  getEnumKeys,
  BidGridDto,
  REVERSE_ORDER_MAP,
  FIAT_CURRENCY_ENUM,
  CONTRACT_CURRENCY_ENUM,
  BALANCE_FILTERS_TITLE,
  NATIVE_TOKENS_ENUM,
} from "interfaces";
import { DirectionIcon, Status, DisplayAmount, getCryptoIconRound } from "ui";
import { dateFormat, getBidFilters, getFilterIcon, getSortIcon } from "utils";
import { UserStore } from "stores/UserStore";
import { BidList } from "stores/BidList";
import "styles/imageStyles.css";
import styles from "./bids.module.css";
import { DIRECT_PAYMENT_CUSTOMER_LP } from "utils/envConstants";
import FileIcon from "images/fileIcon.svg";

export const getColumns = (user: UserStore, bids: BidList, fiatCurrencyList?: any): Array<ColumnType<BidGridDto>> => {
  const { isJuniorLP, isSeniorLP, isCustomer, isLPRiskManager } = user.roles;
  const isLpProvider = isJuniorLP || isSeniorLP || isLPRiskManager;
  const isDirectPaymentCustomerLp = isLpProvider && !DIRECT_PAYMENT_CUSTOMER_LP;

  return [
    {
      dataIndex: "hid",
      key: "hid",
      title: "Номер заявки",
      width: 100,
      render: (_: any, record: BidGridDto) => {
        return (
          <Flex gap="small" className="align-items-center">
            <div className="square icon-3xl bg-basic-200">
              <img src={FileIcon} alt="" />
            </div>
            <Typography.Text>№{record.hid}</Typography.Text>
          </Flex>
        );
      },
    },
    {
      dataIndex: "contractDirection",
      key: "contractDirection",
      title: "Тип",
      width: 60,
      filterMultiple: false,
      filteredValue: bids.queryParams["contractDirection"],
      filtered: "contractDirection" in bids.queryParams,
      filters: getEnumKeys(DIRECTION_TEXT).map((key) => ({
        text: DIRECTION_TEXT[key],
        value: key,
      })),
      filterIcon: (filtered: boolean) => getFilterIcon(filtered),
      render: (_: any, record: BidGridDto) => {
        return <DirectionIcon direction={record.contract.direction} />;
      },
    },
    !isDirectPaymentCustomerLp
      ? {
          dataIndex: "unk",
          key: "unk",
          title: "УНК договора",
          width: 180,
          render: (_: any, record: BidGridDto) => {
            return (
              <Flex vertical gap="small">
                <Typography.Text>{record.contract.unk}</Typography.Text>
                {!isCustomer && (
                  <Tooltip trigger="hover" placement="topLeft" title={record.contract.companyName}>
                    <Typography.Text type="secondary" className={`text-clamp-ellipsis ${styles.companyName}`}>
                      {record.contract.companyName}
                    </Typography.Text>
                  </Tooltip>
                )}
              </Flex>
            );
          },
        }
      : null,
    {
      dataIndex: "fiatAmount",
      key: "fiatCurrency",
      title: "Сумма в валюте договора",
      sorter: { multiple: 3 },
      sortOrder: bids?.sorters?.fiatAmount && REVERSE_ORDER_MAP[bids.sorters.fiatAmount],
      sortIcon: getSortIcon,
      filterMultiple: false,
      filteredValue: bids.queryParams["fiatCurrency"],
      filtered: "fiatCurrency" in bids.queryParams,
      filters: getEnumKeys(FIAT_CURRENCY_ENUM).map((key) => ({
        text: FIAT_CURRENCY_ENUM[key],
        value: key,
      })),
      filterIcon: (filtered: boolean) => getFilterIcon(filtered),
      width: 220,
      render: (_: any, record: BidGridDto) => {
        return (
          <DisplayAmount
            amount={record.fiatAmount}
            currency={record.fiatCurrency}
            showFlag
            squareBg
            withName
            currencyList={fiatCurrencyList?.currencyMap || {}}
          />
        );
      },
    },
    {
      dataIndex: "amount",
      key: "currency",
      title: "Сумма в цифровой валюте",
      sorter: { multiple: 3 },
      sortOrder: bids?.sorters?.amount && REVERSE_ORDER_MAP[bids.sorters.amount],
      sortIcon: getSortIcon,
      filterMultiple: false,
      filteredValue: bids.queryParams["currency"],
      filtered: "currency" in bids.queryParams,
      filters: getEnumKeys(CONTRACT_CURRENCY_ENUM).map((key) => ({
        text: CONTRACT_CURRENCY_ENUM[key],
        value: key,
      })),
      filterIcon: (filtered: boolean) => getFilterIcon(filtered),
      width: 220,
      render: (_: any, record: BidGridDto) => {
        return (
          <DisplayAmount amount={record.amount || 0} currency={record.currency || ""} showFlag squareBg withName />
        );
      },
    },
    {
      dataIndex: "balanceCurrency",
      key: "balanceCurrency",
      title: "Баланс на заявке",
      // sorter: { multiple: 3 },
      // sortOrder: bids?.sorters?.amount && REVERSE_ORDER_MAP[bids.sorters.amount],
      // sortIcon: getSortIcon,
      filterMultiple: true,
      filteredValue: bids.queryParams["balanceCurrency"],
      filtered: "balanceCurrency" in bids.queryParams,
      filterSearch: (value: string, record: { value: string }) => {
        return record.value.toLowerCase().includes(value.toLowerCase());
      },
      filters: getEnumKeys(BALANCE_FILTERS_TITLE)
        .filter((key) => (isCustomer ? !Object.keys(NATIVE_TOKENS_ENUM).includes(key) : key))
        .map((key) => ({
          text: (
            <>
              <div style={{ display: "inline-block" }}>
                <Flex align="center">
                  <span style={{ width: "30px" }}>{key}</span>
                  <Flex
                    align="center"
                    gap="small"
                    justify="flex-end"
                    style={{
                      width: "190px",
                    }}
                  >
                    <span>{BALANCE_FILTERS_TITLE[key]} </span>
                    <span style={{ marginTop: "5px" }}>{getCryptoIconRound(key, "md")}</span>
                  </Flex>
                </Flex>
              </div>
            </>
          ),
          value: key,
        })),
      filterIcon: (filtered: boolean) => getFilterIcon(filtered),
      width: 160,
      render: (_: any, record: BidGridDto) => {
        return (
          <Flex vertical gap={"small"}>
            {record.balance && "rubBalance" in record.balance && Number(record.balance.rubBalance) !== 0 && (
              <DisplayAmount amount={Number(record.balance.rubBalance) || 0} currency={"RUB"} showFlag />
            )}
            {record.nativeBalance !== 0 && !isCustomer && (
              <DisplayAmount amount={record.nativeBalance || 0} currency={record.network || ""} showFlag decimals={3} />
            )}
            {record.tokenBalance !== 0 && (
              <DisplayAmount amount={record.tokenBalance || 0} currency={record.currency ?? ""} showFlag />
            )}
          </Flex>
        );
      },
    },
    {
      dataIndex: "status",
      key: "statuses",
      title: "Статус",
      filterMultiple: true,
      filteredValue: bids.queryParams["statuses"],
      filtered: "status" in bids.queryParams,
      filters: getBidFilters(),
      width: 200,
      align: "right",
      filterIcon: (filtered: boolean) => getFilterIcon(filtered),
      render: (_: any, record: BidGridDto) => {
        const { status, statusDate } = record;
        return (
          <Status
            color={BID_STATUSES.colors[status]}
            children={BID_STATUSES.texts[status]}
            date={dateFormat(statusDate, true)}
          />
        );
      },
    },
  ].filter((c) => !!c) as Array<ColumnType<BidGridDto>>;
};
